import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingSpinner } from './components/UI';

const LoginPage          = lazy(() => import('./pages/LoginPage'));
const DashboardPage      = lazy(() => import('./pages/DashboardPage'));
const ContractDetailPage = lazy(() => import('./pages/ContractDetailPage'));
const UploadPage         = lazy(() => import('./pages/UploadPage'));
const UsersPage          = lazy(() => import('./pages/UsersPage'));
const OrgsPage           = lazy(() => import('./pages/OrgsPage'));
const DashboardsPage     = lazy(() => import('./pages/DashboardsPage'));
const PrivacyPage        = lazy(() => import('./pages/PrivacyPage'));

function ProtectedRoute({ children }) {
  const { user, loading, slowStart } = useAuth();
  if (loading) return <BackendWakeScreen slow={slowStart} />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAdmin, loading, slowStart } = useAuth();
  if (loading) return <BackendWakeScreen slow={slowStart} />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Close sidebar on Escape key (mobile)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', color: 'var(--text-primary)' }}>
      {/* Mobile backdrop */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <Sidebar
        isOpen={sidebarOpen}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`app-main${collapsed ? ' sidebar-collapsed' : ''}`}>
        {/* Hamburger — only visible on mobile via CSS */}
        <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>
          <Menu size={20} />
        </button>

        {children}
      </main>
    </div>
  );
}

function BackendWakeScreen({ slow }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#223040',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 24,
    }}>
      {/* Spinner */}
      <div style={{
        width: 48, height: 48,
        border: '4px solid rgba(242,225,179,0.15)',
        borderTop: '4px solid #126463',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />

      {slow ? (
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <p style={{ color: '#F2E1B3', fontSize: 15, fontWeight: 600, margin: '0 0 6px' }}>
            Iniciando el servidor...
          </p>
          <p style={{ color: 'rgba(242,225,179,0.55)', fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            El servidor estuvo inactivo y está despertando.<br />
            Esto puede tardar hasta 30 segundos.
          </p>
        </div>
      ) : (
        <p style={{ color: 'rgba(242,225,179,0.4)', fontSize: 14, margin: 0 }}>
          Cargando...
        </p>
      )}
    </div>
  );
}

function AppRoutes() {
  const { user, loading, slowStart } = useAuth();
  const location = useLocation();
  if (loading) return <BackendWakeScreen slow={slowStart} />;

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />

          <Route path="/" element={
            <ProtectedRoute>
              <AppLayout><DashboardPage /></AppLayout>
            </ProtectedRoute>
          } />

          <Route path="/contracts/:id" element={
            <ProtectedRoute>
              <AppLayout><ContractDetailPage /></AppLayout>
            </ProtectedRoute>
          } />

          <Route path="/admin/upload" element={
            <AdminRoute>
              <AppLayout><UploadPage /></AppLayout>
            </AdminRoute>
          } />

          <Route path="/admin/users" element={
            <AdminRoute>
              <AppLayout><UsersPage /></AppLayout>
            </AdminRoute>
          } />

          <Route path="/admin/orgs" element={
            <AdminRoute>
              <AppLayout><OrgsPage /></AppLayout>
            </AdminRoute>
          } />

          <Route path="/admin/dashboards" element={
            <AdminRoute>
              <AppLayout><DashboardsPage /></AppLayout>
            </AdminRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
